import { useState, useEffect } from "react";
import { Post as IPost } from "../../@types/Posts";
import { isEmpty, orderBy, slice } from "lodash";
import { Post, PostLoadingSkeleton } from "../../components/Post";
import { fullPageFatalError } from "../../components/AnalysisPageComponents/Cards/ErrorsCard";
import { User } from "../../@types/Users";
import { PostProvider } from "../../context/PostContext";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "react-bootstrap";
import { EmptyState } from "../../components/Post/EmptyState";
import { useHistory } from "react-router-dom";
import usePost from "../../hooks/usePost";
import { useUser } from "../../hooks/useUser";
import useStorage from "../../hooks/useStorage";
import { useTour } from "@reactour/tour";
import useGeneralContext from "../../context/GeneralContext";
import ScrollToTop from "react-scroll-to-top";
import { isMobile } from "react-device-detect";
import { GoMoveToTop } from "react-icons/go";
import useAnalysisTypePreferences from "../../context/AnalysisTypePreferencesContext";

export default function Dashboard() {
  const [user, setUser] = useState<User>();
  const [postsResponse, setPostsResponse] = useState<IPost[]>([]);
  const [visiblePosts, setVisiblePosts] = useState<IPost[]>([]);
  const [maxVisiblePosts, setMaxVisiblePosts] = useState<number>(10);
  const [hasNextPosts, setHasNextPosts] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [hasNoPosts, setHasNoPosts] = useState<boolean>(false);
  const { push } = useHistory();
  const { all: findAllPosts } = usePost();
  const { getUserDetailsWithCache } = useUser();
  const { preventFullLocalStorage } = useStorage();
  const { setIsOpen, setCurrentStep, setSteps, steps } = useTour();
  const { tourStart, setShowTourButtons, setDisablePrev } = useGeneralContext();
  const { setPreferences } = useAnalysisTypePreferences();

  async function determineUser() {
    const userDetails = await getUserDetailsWithCache();

    if (userDetails) {
      const investorType = userDetails.investor_type;

      if (investorType !== 0 && !investorType) {
        push("/welcome");
      }

      setUser(userDetails);

      if (!localStorage.getItem("is_user_invoiced")) {
        localStorage.setItem("is_user_invoiced", "true");
      }
    } else {
      setErrorMsg("Ocorreu um erro desconhecido, por favor, tente novamente.");
    }
  }

  async function refreshPosts() {
    setIsLoading(true);
    setErrorMsg("");

    const posts = await findAllPosts();
    if (posts) {
      if (posts.length === 0) {
        setHasNoPosts(true);
      } else {
        setHasNoPosts(false);
      }

      const orderedPosts = orderBy(posts, (post) => post.created_at, "desc");
      setPostsResponse(orderedPosts);

      const newVisiblePosts = slice(orderedPosts, 0, maxVisiblePosts);
      setVisiblePosts(newVisiblePosts);
    } else {
      setHasNoPosts(true);
    }

    setIsLoading(false);
  }

  function handleLoadMorePosts() {
    const totalExistingPosts = postsResponse.length;
    const totalVisiblePosts = visiblePosts.length;
    const nextTotalVisiblePosts = totalVisiblePosts + 1;
    const hasNextPage = totalExistingPosts > nextTotalVisiblePosts;

    if (hasNextPage) {
      setMaxVisiblePosts((old) => old + 10);
    } else {
      setHasNextPosts(false);
    }
  }

  useEffect(() => {
    const tourSteps = [
      {
        selector: "#step-search-icon",
        content: "Clique na lupa para abrir a caixa de pesquisa de ativos.",
        position: "bottom",
        action: () => {
          setShowTourButtons(false);
          if (localStorage.getItem("analysis_type_preferences")) {
            setPreferences({
              technical: true,
              fundamental: true,
              health: true,
              extra: true,
            })
          }
        },
        actionAfter: () => {
          document.getElementById("step-search-icon")?.click();
          document.body.classList.add("show-popup-search-persist");
          setShowTourButtons(true);
        },
      },
      {
        selector: "#step-search-bar",
        content:
          "Aqui você consegue pesquisar seus ativos favoritos, seja uma ação, criptomoeda ou até mesmo forex.",
        position: "top",
        action: () => {
          setDisablePrev(true);
          document
            .querySelector(".dropdown-menu input")
            ?.classList.add("disabled");
        },
        actionAfter: () => {
          document
            .querySelector(".dropdown-menu input")
            ?.classList.remove("disabled");
        },
      },
      {
        selector: "#first-suggestion",
        content:
          "Após pesquisar, clique no ativo que você deseja realizar uma análise profunda.",
        action: () => {
          setShowTourButtons(false);
        },
        actionAfter: () => {
          document.body.classList.remove("show-popup-search-persist");
          push("/analysis/anorf");
          setIsOpen(false);
          if (setSteps) {
            setSteps([]);
          }
        },
      },
    ];

    if (tourStart) {
      document.body.classList.add("body-overflow");
      setIsOpen(true);
      setCurrentStep(0);
      if (setSteps && !steps.length) {
        // @ts-ignore reason: ignored as this is a bug in @reactour
        setSteps(tourSteps);
      }
    } else {
      document.body.classList.remove("body-overflow");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tourStart,
    setCurrentStep,
    setIsOpen,
    setSteps,
    steps.length,
    setShowTourButtons,
    push,
  ]);

  useEffect(() => {
    preventFullLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEmpty(postsResponse)) {
      refreshPosts();
    }

    if (!user) {
      determineUser();
    }

    if (postsResponse && visiblePosts && maxVisiblePosts > 10) {
      const newVisiblePosts = slice(postsResponse, 0, maxVisiblePosts);
      setTimeout(() => {
        setVisiblePosts(newVisiblePosts);
      }, 500);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxVisiblePosts]);

  //Check the local storage memory usage.

  if (isLoading) {
    return (
      <div className="az-content pd-t-20">
        {[0, 1, 2, 3].map((key) => {
          return <div key={key}>{PostLoadingSkeleton()}</div>;
        })}
      </div>
    );
  } else if (!isEmpty(errorMsg)) {
    return fullPageFatalError();
  } else if (hasNoPosts) {
    return <EmptyState doRefreshPosts={refreshPosts} />;
  } else {
    return (
      <div className="az-content pd-t-20" id="feed">
        <InfiniteScroll
          dataLength={visiblePosts.length}
          next={handleLoadMorePosts}
          hasMore={hasNextPosts}
          refreshFunction={refreshPosts}
          loader={
            <div className="text-center mg-t-20 mg-b-20">
              <Spinner animation="border"></Spinner>
            </div>
          }
          pullDownToRefresh={false}
          pullDownToRefreshThreshold={100}
          pullDownToRefreshContent={
            <h3 style={{ textAlign: "center" }}>
              &#8595; Puxe para baixo para atualizar
            </h3>
          }
          releaseToRefreshContent={
            <h3 style={{ textAlign: "center" }}>
              &#8593; Solte para atualizar
            </h3>
          }
        >
          {visiblePosts.map((post, i) => {
            if (user) {
              return (
                <div key={i}>
                  <PostProvider>
                    <Post post={post} user={user} />
                  </PostProvider>
                </div>
              );
            } else {
              return <div key={i}></div>;
            }
          })}
        </InfiniteScroll>
        <ScrollToTop
          style={{
            backgroundColor: "var(--bg-3)",
            boxShadow: "0 2px 10px 0 rgb(94 92 115 / 50%)",
          }}
          top={isMobile ? 2000 : 5000}
          component={<GoMoveToTop size={22} color="var(--text-2" />}
          smooth
        />
      </div>
    );
  }
}
